<template>
  <b-dropdown
    split
    :split-class="buttonClasses"
    :toggle-class="buttonClasses"
    class="split-button"
    :class="{ 'disabled-split-button': props.disabled }"
    :disabled="!canImport"
    @click.prevent="beginImport(TranslationFileType.Excel)"
    v-tooltip="$t(props.translationGroup ? 'translationManager.importTable' : 'translationManager.importProject')">
    <template #button-content>
      <Icon icon="Import" /><span v-if="!props.iconOnly">{{
        $t(props.translationGroup ? "translationManager.importTable" : "translationManager.importProject")
      }}</span>
    </template>
    <b-dropdown-item link-class="icon-item" @click.prevent="beginImport(TranslationFileType.Excel)">
      <div class="dd-item-container">
        <span class="dd-item-icon"><Icon icon="FileExcel"></Icon></span>
        <span class="dd-item-text">{{ $t("translationManager.exportExcel") }}</span>
      </div>
    </b-dropdown-item>
    <b-dropdown-item link-class="icon-item" @click.prevent="beginImport(TranslationFileType.XLIFF)">
      <div class="dd-item-container">
        <span class="dd-item-icon"><Icon icon="Export"></Icon></span>
        <span class="dd-item-text">{{ $t("translationManager.exportXliff") }}</span>
      </div>
    </b-dropdown-item>
  </b-dropdown>
  <ProgressModal :progress="vm.progressHandler.progress" @cancel="importCancelled" :min-open-time="1000" :auto-close="true"></ProgressModal>
</template>

<script setup lang="ts">
import Icon from "@common/components/Icon.vue";
import ProgressModal from "@common/components/ProgressModal.vue";
import { useToast } from "@common/composable/toast";
import { useGlobalParameters } from "@common/composable/useRootStore";
import { ActivityCriteria } from "@medapro-types/common/activity/activity";
import { ActivityAction, ActivityObject, ActivityProcess, ActivitySeverity, ActivityStatus } from "@medapro-types/common/activity/activity-enums";
import { ProgressHandler } from "@medapro-types/common/components/ProgressHandling";
import { TranslationFileType, TranslationGroupType } from "@medapro-types/TranslationManager/translationManager";
import activityApi from "@services/activity";
import applications from "@services/applications";
import { useTranslationManagerActions } from "@TranslationManager/composables/useTranslationManagerStore";
import { useFileDialog } from "@vueuse/core";
import { computed, reactive } from "vue";
import { useI18n } from "vue-i18n";

//#region General
const props = withDefaults(
  defineProps<{
    id: string;
    translationProjectId?: string | null;
    translationGroup?: TranslationGroupType | null;
    dataChangeAllowedCheck: () => boolean;
    disabled?: boolean;
    iconOnly?: boolean;
  }>(),
  {
    disabled: false,
    iconOnly: true
  }
);

const { t: $t } = useI18n();
const { createToast } = useToast();
//#endregion

//#region Store
const { importTranslations } = useTranslationManagerActions();
const { globalParameters } = useGlobalParameters();
//#endregion

//#region ViewModel

type ViewModel = {
  progressHandler: ProgressHandler;
};

const vm = reactive<ViewModel>({
  progressHandler: ProgressHandler.createIndeterminate({ id: `${props.id}-button-progress-modal`, isCancellable: true })
});
//#endregion

//#region Import
const importCancelled = () => {
  vm.progressHandler.cancel(`${$t("translationManager.cancellingImport")}...`);
};

const canImport = computed(() => {
  return props.translationProjectId && !props.disabled;
});

const onFileSelected = async (files: FileList | null, fileType: TranslationFileType) => {
  if (canImport.value && files?.length) {
    const fileToImport = files[0];

    try {
      await importTranslations({
        fileType: fileType,
        fileToImport: fileToImport,
        translationGroup: props.translationGroup,
        progressHandler: vm.progressHandler
      });
    } catch (error: any) {
      createToast({
        text: error?.message || $t("UploadFailed"),
        title: $t("Error"),
        variant: "danger",
        titleIcon: "Translation-Manager",
        textIllustration: "Error",
        statusTextIcon: "XCircleSolid"
      });
      return;
    }

    await createActivityEntry(fileToImport.name);
  }
};

const createActivityEntry = async (filename: string) => {
  try {
    if (globalParameters.value.IsActivityFeedEnabled) {
      const activityCriterias: ActivityCriteria[] = [
        {
          severity: ActivitySeverity.Information,
          process: ActivityProcess.Import,
          objectType: ActivityObject.File,
          status: ActivityStatus.Successful,
          action: ActivityAction.Completed,
          descriptionValues: [filename],
          descriptionKey: "TranslationProjectImported",
          objectValue: filename,
          applicationId: applications.Modules.TranslationManager.Id
        }
      ];

      await activityApi.createActivity(activityCriterias);
    }
  } catch (error) {
    console.error("Activity insertion for translation project import failed with error : ", error);
  }
};

const beginImport = (fileType: TranslationFileType) => {
  if (!canImport.value && !props.dataChangeAllowedCheck()) {
    return;
  }

  const mimeTypes =
    fileType === TranslationFileType.XLIFF
      ? ["application/xliff+xml", ".xlf"]
      : ["application/vnd.ml-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "application/x-excel"];
  const accept = mimeTypes.toString();
  const { open, onChange } = useFileDialog({
    accept: accept,
    multiple: false
  });

  onChange((fileList) => {
    onFileSelected(fileList, fileType);
  });

  open();
};

const buttonClasses = computed(() => {
  return `mono ${props.iconOnly ? "btn-icon" : "btn-with-icon"}`;
});
//#endregion
</script>
